import React from 'react';

/**
 * LegalDocumentRenderer - Renders legal document content with proper formatting
 * 
 * Handles:
 * - Document title (first lines before numbered sections) - can be skipped if shown elsewhere
 * - Numbered section headings (e.g., "1. About Us", "2. Acceptance")
 * - Sub-section headings (e.g., "4.1 Account Responsibility")
 * - Bullet points and lists
 * - Regular paragraphs
 * - Proper spacing between sections
 * 
 * @param {string} content - The legal document content
 * @param {boolean} skipTitle - If true, skips rendering the title/intro section
 */
const LegalDocumentRenderer = ({ content, skipTitle = false }) => {
  if (!content) return null;

  // Split content into paragraphs (double newlines are paragraph breaks)
  const sections = content.split(/\n\n+/);

  // Check if a line is a numbered section heading
  const isNumberedHeading = (line) => /^\d+\.\s+/.test(line.trim());
  
  // Check if a line is a sub-section heading
  const isSubHeading = (line) => /^\d+\.\d+\s+/.test(line.trim());

  const renderSection = (section, sectionIndex, isTitle = false) => {
    const lines = section.split('\n');
    const elements = [];
    let currentList = [];

    lines.forEach((line, lineIndex) => {
      const trimmedLine = line.trim();
      if (!trimmedLine) return;

      const isBullet = trimmedLine.match(/^[-•●]\s+/) || trimmedLine.match(/^[a-z]\)\s+/i);

      if (isBullet) {
        currentList.push(
          <li key={`${sectionIndex}-${lineIndex}`} className="text-[#E2E8F0] text-sm mb-1">
            {trimmedLine.replace(/^[-•●]\s+/, '').replace(/^[a-z]\)\s+/i, '')}
          </li>
        );
      } else {
        // Flush current list if any
        if (currentList.length > 0) {
          elements.push(
            <ul key={`list-${sectionIndex}-${lineIndex}`} className="list-disc pl-6 mb-3 space-y-1">
              {currentList}
            </ul>
          );
          currentList = [];
        }

        // Check if it's a main heading (numbered like "1. About Us")
        const mainHeadingMatch = trimmedLine.match(/^(\d+)\.\s+(.+)$/);
        if (mainHeadingMatch) {
          elements.push(
            <h2 key={`${sectionIndex}-${lineIndex}`} className="text-base font-bold text-white mt-6 mb-2 first:mt-0">
              {trimmedLine}
            </h2>
          );
        }
        // Check if it's a sub-heading (like "4.1 Account Responsibility")
        else if (trimmedLine.match(/^(\d+\.\d+)\s+(.+)$/)) {
          elements.push(
            <h3 key={`${sectionIndex}-${lineIndex}`} className="text-sm font-semibold text-[#A0AEC0] mt-4 mb-2">
              {trimmedLine}
            </h3>
          );
        }
        // Title section - render as main document heading
        else if (isTitle) {
          // Check if it looks like a document title (not a date or company name detail)
          const isMainTitle = trimmedLine.toLowerCase().includes('terms') || 
                              trimmedLine.toLowerCase().includes('privacy') || 
                              trimmedLine.toLowerCase().includes('cookie') ||
                              trimmedLine.toLowerCase().includes('policy') ||
                              trimmedLine.toLowerCase().includes('agreement');
          
          if (isMainTitle) {
            elements.push(
              <h1 key={`${sectionIndex}-${lineIndex}`} className="text-xl font-bold text-white mb-4">
                {trimmedLine}
              </h1>
            );
          } else if (trimmedLine.toLowerCase().includes('effective date') || trimmedLine.toLowerCase().includes('last updated')) {
            elements.push(
              <p key={`${sectionIndex}-${lineIndex}`} className="text-[#A0AEC0] text-xs mb-3">
                {trimmedLine}
              </p>
            );
          } else {
            elements.push(
              <p key={`${sectionIndex}-${lineIndex}`} className="text-[#E2E8F0] text-sm leading-relaxed mb-1">
                {trimmedLine}
              </p>
            );
          }
        }
        // Regular text
        else {
          elements.push(
            <p key={`${sectionIndex}-${lineIndex}`} className="text-[#E2E8F0] text-sm leading-relaxed">
              {trimmedLine}
            </p>
          );
        }
      }
    });

    // Flush any remaining list items
    if (currentList.length > 0) {
      elements.push(
        <ul key={`list-${sectionIndex}-end`} className="list-disc pl-6 mb-3 space-y-1">
          {currentList}
        </ul>
      );
    }

    return (
      <div key={sectionIndex} className={isTitle ? "mb-6" : "mb-4"}>
        {elements}
      </div>
    );
  };

  // Find the first numbered section to separate title/intro from main content
  let titleEndIndex = 0;
  for (let i = 0; i < sections.length; i++) {
    const lines = sections[i].split('\n');
    if (lines.some(line => isNumberedHeading(line))) {
      titleEndIndex = i;
      break;
    }
    titleEndIndex = i + 1;
  }

  return (
    <div className="legal-document">
      {/* Title/Intro sections - only render if not skipping */}
      {!skipTitle && sections.slice(0, titleEndIndex).map((section, index) => 
        renderSection(section, index, true)
      )}
      
      {/* Main content sections */}
      {sections.slice(titleEndIndex).map((section, index) => 
        renderSection(section, index + titleEndIndex, false)
      )}
    </div>
  );
};

export default LegalDocumentRenderer;
